import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { ConfirmationAction } from '@/features/confirmation-action';
import { capacityLevel } from '@/entities/consignments';

import type { LocalPallet } from '../model/types';

interface PalletHeaderProps {
  pallet: LocalPallet;
  lineCount: number;
  isExpanded: boolean;
  /** A save is running: the pallet cannot be deleted until it ends, not even from a dialog opened before */
  isSaving?: boolean;
  onToggle: () => void;
  /** Omitted when the user may not delete the pallet */
  onDelete?: () => Promise<void>;
}

/** `840.00 / 1500 kg`; the cap is left out when the pallet type has none. */
const load = (current: number, max: number | null, digits: number, unit: string) =>
  `${current.toFixed(digits)}${max != null ? ` / ${max}` : ''} ${unit}`;

interface PalletLoadProps {
  dimension: 'weight' | 'volume';
  current: number;
  max: number | null;
}

const BADGE_VARIANT = { full: 'destructive', nearlyFull: 'warning' } as const;
/** Tooltip key prefix per level; the key also names the dimension, whose unit the text gives */
const TOOLTIP_KEY = { full: 'pallet.capacity.over', nearlyFull: 'pallet.capacity.nearlyFull' } as const;

/**
 * Weight or volume on the pallet against the pallet type's capacity: a red badge
 * when full, a yellow one when nearly full (`capacityLevel`), with a tooltip
 * saying why.
 */
function PalletLoad({ dimension, current, max }: PalletLoadProps) {
  const { t } = useTranslation();
  const isWeight = dimension === 'weight';
  const text = `${t(isWeight ? 'bundleType.bundleWeight.label' : 'bundleType.bundleVolume.label')}: ${load(
    current,
    max,
    isWeight ? 2 : 4,
    isWeight ? 'kg' : 'm³'
  )}`;

  const { level, percent } = capacityLevel(current, max);
  if (level === 'normal') return <span>{text}</span>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={BADGE_VARIANT[level]} className="text-sm">
          {text}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{t(`${TOOLTIP_KEY[level]}${isWeight ? 'Weight' : 'Volume'}`, { max, percent })}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A pallet's row: one line on a wide screen; on a phone the loads wrap under the
 * name and the line count stays top right. The type is named by legacy RITA's
 * own type names, or the backend description for a type added later.
 */
export function PalletHeader({
  pallet,
  lineCount,
  isExpanded,
  isSaving = false,
  onToggle,
  onDelete
}: PalletHeaderProps) {
  const { t } = useTranslation();
  const typeLabel = t(`overpackingType.${pallet.typeId}`, { defaultValue: pallet.type ?? '' });

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-muted/40 border border-border rounded-lg hover:bg-muted/60 transition-colors">
      <button
        type="button"
        className="flex flex-1 min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-left cursor-pointer"
        onClick={onToggle}>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        {typeLabel && <span className="font-semibold text-sm whitespace-nowrap">{typeLabel}</span>}
        <span className="text-sm">{pallet.palletNumber}</span>
        <span className="text-xs text-muted-foreground whitespace-nowrap ml-auto md:order-last">
          {t('pallet.lineCount', { count: lineCount })}
        </span>
        <span className="flex basis-full flex-wrap items-center gap-x-2 gap-y-1 pl-6 text-sm md:basis-auto md:pl-0">
          <span className="max-md:hidden">|</span>
          <PalletLoad dimension="weight" current={pallet.currentWeight} max={pallet.maxWeight} />
          <span className="max-md:hidden">|</span>
          <PalletLoad dimension="volume" current={pallet.currentVolume} max={pallet.maxVolume} />
        </span>
      </button>
      {onDelete && (
        <ConfirmationAction
          trigger={
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
              disabled={isSaving}
              aria-label={t('gen.delete.label')}>
              <Trash2 className="h-4 w-4" />
            </Button>
          }
          title={t('pallet.delete.title', { pallet: pallet.palletNumber })}
          description={lineCount > 0 ? t('pallet.delete.description', { count: lineCount }) : undefined}
          confirmText={t('gen.delete.label')}
          confirmIntent="danger-confirm"
          isLoading={isSaving}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}
